import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis,
} from 'recharts';
import type { ChartConfig } from '../../../types';
import { CHART_STYLE, formatAxisNumber, formatDataValue, analyzeDecimals } from './chartStyles';

interface ScatterChartViewProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
  bubble?: boolean;
}

/**
 * Scatter plot (Rule 4a) and bubble chart (Rule 4b).
 * For bubble charts, the sizeAxis maps to the ZAxis (dot size).
 */
export function ScatterChartView({ data, config, bubble }: ScatterChartViewProps) {
  const xKey = config.xAxis ?? '';
  const yKey = typeof config.yAxis === 'string' ? config.yAxis : config.yAxis?.[0] ?? '';
  const sizeKey = config.sizeAxis;
  const { hasDecimals: xDec, allSmall: xSmall } = analyzeDecimals(data, xKey);
  const { hasDecimals: yDec, allSmall: ySmall } = analyzeDecimals(data, yKey);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ScatterChart margin={CHART_STYLE.margin}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis
          dataKey={xKey}
          name={xKey}
          tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.xAxis.tickFill }}
          tickFormatter={formatAxisNumber}
          type="number"
        />
        <YAxis
          dataKey={yKey}
          name={yKey}
          tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.yAxis.tickFill }}
          width={CHART_STYLE.yAxis.width}
          tickFormatter={formatAxisNumber}
          type="number"
        />
        {bubble && sizeKey && (
          <ZAxis dataKey={sizeKey} name={sizeKey} range={[40, 400]} />
        )}
        <Tooltip
          contentStyle={CHART_STYLE.tooltip}
          formatter={(value, name) => {
            const n = name ?? '';
            const isX = n === xKey;
            return [
              formatDataValue(Number(value ?? 0), isX ? xDec : yDec, isX ? xSmall : ySmall),
              n,
            ];
          }}
        />
        <Scatter
          data={data}
          fill={CHART_STYLE.colors[0]}
          fillOpacity={0.7}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
}
